import { useEffect, createContext, useContext } from "react";
import { useLocation } from "react-router";
import { useAuth } from "./AuthContext";
import { getAppVersion } from "../lib/supabase";


const CacheBusterContext = createContext({ refreshApp: () => { } });

export const useCacheBuster = () => useContext(CacheBusterContext);

const CacheBuster = ({ children }: { children: React.ReactNode }) => {
	const { session } = useAuth();
	const location = useLocation();

	useEffect(() => {
		const abortController = new AbortController();

		if (!session) return;
		checkVersionUpdate(abortController.signal);

		// when effect finishes, call abort to cancel execution of other checkVersionUpdate calls in progress
		return () => abortController.abort();
	}, [location]);

	/**
	 * Check if the current version is the latest version
	 * @returns {Promise<void>}
	 */
	const checkVersionUpdate = async (signal: AbortSignal): Promise<void> => {

		const version = await getAppVersion(signal);

		if (!version) return

		const latestVersion = version.version;
		const localVersion = localStorage.getItem("app_versions");
		const currentVersion = localVersion ? JSON.parse(localVersion).version : 0;

		if (!localVersion) {
			localStorage.setItem("app_versions", JSON.stringify({ id: version.id, version: latestVersion }));
			return;
		}

		// check if localVersion is out of date
		if (latestVersion?.toString() !== currentVersion?.toString()) {
			// ensure that another checkVersionUpdate hasn't been called
			if (signal.aborted) {
				// Call aborted indicating a race condition - stop execution silently
				return;
			}

			refreshApp();
			localStorage.setItem("app_versions", JSON.stringify({ id: version.id, version: latestVersion }));

		}
	};

	const refreshApp = () => {
		if (caches) {
			caches.keys().then((names) => {
				for (let name of names) caches.delete(name);
			});
		}

		window.location.reload();
	};

	return <CacheBusterContext.Provider value={{ refreshApp }}>{children}</CacheBusterContext.Provider>;
};

export default CacheBuster;
